// The payment-status vocabulary + pure classifiers, ported NEARLY VERBATIM from
// the MBSPSU gen-1 fork (apps/portal-api/src/modules/payments/payment-status.ts,
// per the fin-design SOURCE-SCOUT correction 1) — dependency-free besides zod,
// browser-safe, and locked by its ported test suite.
//
// The manual v1 path uses: pending_verification → paid | verification_rejected,
// plus 'cancelled' (the payer withdrew while still pending_verification).
// 'pending' and 'expired' are RESERVED for the future gateway-link adapters
// (Razorpay/PhonePe/Stripe — the §6.3 PaymentIntent Created/Expired states);
// 'refunded' is RESERVED for the refund slice (columns only in v1). The
// vocabulary ships whole so gateway adapters slot in without a migration.
import { z } from "zod";

export const PAYMENT_STATUSES = [
  "pending",
  "pending_verification",
  "paid",
  "verification_rejected",
  "expired",
  "cancelled",
  "refunded",
] as const;

export const PaymentStatus = z.enum(PAYMENT_STATUSES);
export type PaymentStatus = z.infer<typeof PaymentStatus>;

/** No DB CHECK constraint backs the column (the source's choice, kept) — assert at every write boundary instead. */
export const isPaymentStatus = (v: unknown): v is PaymentStatus =>
  typeof v === "string" && (PAYMENT_STATUSES as readonly string[]).includes(v);

export function assertPaymentStatus(v: unknown): PaymentStatus {
  if (!isPaymentStatus(v)) throw new RangeError(`Unknown payment status: ${String(v)}`);
  return v;
}

/**
 * ONLY 'paid' counts as collected money (the source :100 law): a claim awaiting
 * verification is NOT revenue, a refunded row is no longer revenue. "Paid is set
 * only by the verified path, never the client" (ADR-000 §6.3).
 */
export const isCollected = (status: PaymentStatus): boolean => status === "paid";

/** The refund classifier (source :110) — v1 reserves the status; the classifier ports with the vocabulary. */
export const isRefund = (status: PaymentStatus): boolean => status === "refunded";

/** The checker-queue predicate (source :133): a manual claim waiting for maker-checker verification. */
export const isAwaitingVerification = (status: PaymentStatus): boolean => status === "pending_verification";

/**
 * Net collected = Σ paid − Σ refunded, floored at 0 (source :145 — a refund
 * storm must never render negative revenue). Integer paise in, integer paise out.
 */
export function netCollectedPaise(rows: readonly { status: PaymentStatus; amountPaise: number }[]): number {
  let gross = 0;
  let refunded = 0;
  for (const r of rows) {
    if (isCollected(r.status)) gross += r.amountPaise;
    else if (isRefund(r.status)) refunded += r.amountPaise;
  }
  return Math.max(0, gross - refunded);
}
